import type { BriefingView, CardView, EditionType, EntryChange } from './api';

/**
 * Daily edition slots, in publication order. The API names each edition; this
 * table is the fallback for responses that do not (older backends, links).
 * "morning" is the legacy morning edition and sorts where the early edition does.
 */
export const SLOT_ORDER = ['early_morning', 'pre_market', 'market_opening', 'lunch', 'afternoon', 'market_close', 'evening'] as const;

const LABELS: Record<string, string> = {
  early_morning: 'Frühausgabe',
  pre_market: 'Vorbörse',
  market_opening: 'Börsenstart',
  lunch: 'Mittagsausgabe',
  afternoon: 'Nachmittagsausgabe',
  market_close: 'Börsenschluss',
  evening: 'Abendausgabe',
  morning: 'Morgenausgabe',
};

/** A short name for tight spaces (the slot strip). */
const SHORT: Record<string, string> = {
  early_morning: 'Früh',
  pre_market: 'Vorbörse',
  market_opening: 'Börsenstart',
  lunch: 'Mittag',
  afternoon: 'Nachmittag',
  market_close: 'Börsenschluss',
  evening: 'Abend',
  morning: 'Morgen',
};

export function editionLabel(type: EditionType | undefined | null, fromApi?: string | null): string {
  if (fromApi) return fromApi;
  if (!type) return 'Ausgabe';
  return LABELS[type] ?? type;
}

export function shortLabel(type: string): string {
  return SHORT[type] ?? LABELS[type] ?? type;
}

export function slotRank(type: string): number {
  const key = type === 'morning' ? 'early_morning' : type;
  const i = (SLOT_ORDER as readonly string[]).indexOf(key);
  return i === -1 ? SLOT_ORDER.length : i;
}

export function changeOf(cv: CardView): EntryChange | undefined {
  return cv.entry?.score?.change;
}

/** A context entry: shown before, unchanged, repeated as background on purpose. */
export function isContext(cv: CardView): boolean {
  return cv.entry?.section === 'context' || changeOf(cv)?.kind === 'context';
}

/** Context entries of an edition in rank order; never part of the topic board. */
export function contextEntries(view: BriefingView): CardView[] {
  const out: CardView[] = [];
  for (const s of view.sections ?? []) if (s.section === 'context') out.push(...(s.cards ?? []));
  return out.sort((a, b) => (a.entry?.position ?? 0) - (b.entry?.position ?? 0));
}

export type BadgeKind = 'new' | 'update' | 'report' | 'context';

/**
 * The reader-facing words for an entry's change, or null when there is none to
 * say. "Aktualisiert" is used only for a stated development; a new report
 * without a read summary is "Neuer Bericht"; a fresher reading with nothing
 * changed is context.
 */
export function changeBadge(change: EntryChange | undefined): { label: string; kind: BadgeKind } | null {
  switch (change?.kind) {
    case 'new':
      return { label: 'Neu', kind: 'new' };
    case 'update':
      if (change.basis === 'new_report') return { label: 'Neuer Bericht', kind: 'report' };
      // Editions before v0.6 marked a newer CrossFit capture as an update
      // without comparing it; that is freshness, not a stated change.
      if (change.basis === 'new_capture' && !(change.what_changed ?? []).length) return { label: 'Neu erfasst', kind: 'context' };
      return { label: 'Aktualisiert', kind: 'update' };
    case 'context':
      if (change.basis === 'recapture_unchanged') return { label: 'Neu gelesen', kind: 'context' };
      return { label: 'Zum Einordnen', kind: 'context' };
    default:
      return null;
  }
}

/**
 * The developments of an update and the reports added, kept apart. Editions
 * before v0.6 stored a new report as a "Neuer Bericht – …" line among the
 * developments; it is shown as a source here, not as something that happened.
 */
export function changeParts(change: EntryChange | undefined): { developments: string[]; reports: string[] } {
  const developments: string[] = [];
  const reports: string[] = [];
  for (const w of change?.what_changed ?? []) {
    if (!w) continue;
    if (w.startsWith('Neuer Bericht – ')) reports.push(w.slice('Neuer Bericht – '.length));
    else developments.push(w);
  }
  for (const s of change?.sources_added ?? []) {
    const label = s.source_name ? `${s.source_name}: ${s.title}` : s.title;
    if (!reports.includes(label)) reports.push(label);
  }
  return { developments, reports };
}
